import { Alert, Select, Text } from '@mantine/core';
import type { Cohort } from '@marketing-tool/shared';
import { useMemo } from 'react';

import { useCohorts } from '../../data/game-report';
import * as remote from '../../data/remote-data';

export const CohortSelector = ({
    id,
    gameId,
    value,
    onChange,
    label,
    clearable,
}: {
    id: string;
    gameId: string;
    value?: string;
    onChange: (cohort: Cohort | undefined) => void;
    label?: string;
    clearable?: boolean;
}) => {
    const cohorts = useCohorts(gameId);
    const data = useMemo(
        () =>
            remote.map(cohorts.data, (cohorts) =>
                cohorts.map((cohort) => ({
                    value: cohort.id,
                    label: `${cohort.name} (${cohort.size.toLocaleString()} users)`,
                })),
            ),
        [cohorts.data],
    );

    return remote.matchAll(data, {
        NotAsked: () => null,
        Loading: () => (
            <Text c="dimmed" fs="italic">
                Loading cohorts...
            </Text>
        ),
        Failure: (error) => (
            <Alert color="red" title="Error loading cohorts">
                {error}
            </Alert>
        ),
        Success: (data) => (
            <Select
                id={id}
                label={label}
                value={value ?? null}
                onChange={(id) => {
                    if (!id) {
                        onChange(undefined);
                        return;
                    }
                    const cohort = remote.withDefault(cohorts.data, []).find((cohort) => cohort.id === id);
                    onChange(cohort);
                }}
                data={data}
                placeholder="Select a cohort"
                clearable={clearable}
            />
        ),
    });
};
